/* eslint-disable unicorn/filename-case -- kebab-case per docs/conventions/core.md: this file
   exports a hook, not a React component, so the PascalCase-for-components rule does not apply. */
import * as React from 'react';

interface LatestFetchState<TChild> {
  readChildRows: () => Readonly<Record<string, TChild[] | undefined>>;
  readLoadingParentIds: () => Readonly<Record<string, boolean>> | undefined;
}

/**
 * Reads the current lazy-fetch state without making it a hook dependency.
 *
 * `childRowsByParentId` and `loadingParentIds` get a fresh object identity on every fetch
 * transition. Listing them as deps of the expand handler would recreate it — and therefore the
 * prepended expand column — twice per fetch, making TanStack rebuild its column model for the whole
 * row set while the user watches child rows appear.
 */
export function useLatestFetchState<TChild>(
  childRowsByParentId: Readonly<Record<string, TChild[] | undefined>>,
  loadingParentIds: Readonly<Record<string, boolean>> | undefined,
): LatestFetchState<TChild> {
  const childRowsRef = React.useRef(childRowsByParentId);
  const loadingParentIdsRef = React.useRef(loadingParentIds);

  // Layout, not passive: a passive effect runs after paint, so an event handler firing between
  // commit and that effect reads pre-commit state -- an already-cached row would read as uncached
  // and its stale in-flight record would never be released.
  React.useLayoutEffect(() => {
    childRowsRef.current = childRowsByParentId;
    loadingParentIdsRef.current = loadingParentIds;
  }, [childRowsByParentId, loadingParentIds]);

  return React.useMemo(
    () => ({
      readChildRows: () => childRowsRef.current,
      readLoadingParentIds: () => loadingParentIdsRef.current,
    }),
    [],
  );
}
